import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  HelpCircle, Plus, Send, MessageSquare, Clock, CheckCircle2,
  AlertCircle, Loader2, ChevronRight, ArrowLeft, Search, Filter,
  LifeBuoy, BookOpen, Bug, Settings, X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { toast } from "sonner";

const statusStyle = {
  open: { bg: "bg-blue-500/10", text: "text-blue-600", label: "Open", icon: AlertCircle },
  in_progress: { bg: "bg-amber-500/10", text: "text-amber-600", label: "In Progress", icon: Clock },
  resolved: { bg: "bg-green-500/10", text: "text-green-600", label: "Resolved", icon: CheckCircle2 },
  closed: { bg: "bg-slate-100", text: "text-slate-500", label: "Closed", icon: CheckCircle2 },
};

const categoryIcons = {
  general: HelpCircle,
  technical: Bug,
  course_content: BookOpen,
  account: Settings,
  other: LifeBuoy,
};

const LearnerSupportPage = () => {
  const { user } = useAuth();
  const variant = user?.role === "manager" ? "manager" : "learner";
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // New ticket form
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newCategory, setNewCategory] = useState("general");
  const [newPriority, setNewPriority] = useState("normal");
  const [creating, setCreating] = useState(false);

  const fetchTickets = useCallback(async () => {
    try {
      const res = await api.get("/support/tickets");
      setTickets(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const loadTicketDetail = async (ticketId) => {
    setSelectedTicket(ticketId);
    setLoadingDetail(true);
    try {
      const res = await api.get(`/support/tickets/${ticketId}`);
      setTicketDetail(res.data);
    } catch { toast.error("Failed to load ticket"); }
    finally { setLoadingDetail(false); }
  };

  const createTicket = async () => {
    if (!newTitle.trim() || !newDesc.trim()) {
      toast.error("Please fill in title and description");
      return;
    }
    setCreating(true);
    try {
      await api.post("/support/tickets", {
        subject: newTitle, description: newDesc, category: newCategory, priority: newPriority,
      });
      toast.success("Ticket created! Our team will respond shortly.");
      setCreateOpen(false);
      setNewTitle(""); setNewDesc(""); setNewCategory("general"); setNewPriority("normal");
      fetchTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create ticket");
    } finally { setCreating(false); }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${selectedTicket}/comments`, { content: replyText });
      toast.success("Reply sent");
      setReplyText("");
      loadTicketDetail(selectedTicket);
    } catch { toast.error("Failed to send reply"); }
    finally { setSending(false); }
  };

  const filtered = tickets.filter(t => {
    const title = t.subject || t.title || "";
    const num = t.ticket_number || t.id || "";
    if (search && !title.toLowerCase().includes(search.toLowerCase()) && !num.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    return true;
  });

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === "open").length,
    in_progress: tickets.filter(t => t.status === "in_progress").length,
    resolved: tickets.filter(t => t.status === "resolved" || t.status === "closed").length,
  };

  // Ticket detail view
  if (selectedTicket && ticketDetail) {
    const style = statusStyle[ticketDetail.status] || statusStyle.open;
    const StatusIcon = style.icon;
    const ticketTitle = ticketDetail.subject || ticketDetail.title || "Ticket";
    const messages = ticketDetail.comments || ticketDetail.messages || [];
    // Include initial description as first message
    const allMessages = [
      {
        id: "initial",
        sender_id: ticketDetail.created_by,
        sender_name: ticketDetail.created_by_name || "You",
        sender_role: "learner",
        message: ticketDetail.description,
        created_at: ticketDetail.created_at,
      },
      ...messages.map(c => ({
        id: c.id,
        sender_id: c.author_id,
        sender_name: c.author_name,
        sender_role: c.author_role === "super_admin" || c.author_role === "tenant_admin" ? "admin" : c.author_role,
        message: c.content,
        created_at: c.created_at,
      })),
    ];
    return (
      <AdminLayout title="Support" variant={variant}>
        <div className="space-y-4 max-w-3xl" data-testid="ticket-detail">
          <Button variant="ghost" size="sm" onClick={() => { setSelectedTicket(null); setTicketDetail(null); }} data-testid="back-to-tickets">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Tickets
          </Button>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{ticketDetail.ticket_number || ticketDetail.id?.slice(0,8)}</p>
                  <CardTitle className="text-lg">{ticketTitle}</CardTitle>
                </div>
                <Badge className={`${style.bg} ${style.text}`}>
                  <StatusIcon className="w-3 h-3 mr-1" /> {style.label}
                </Badge>
              </div>
              <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                <span>Category: {ticketDetail.category}</span>
                <span>Priority: {ticketDetail.priority}</span>
                <span>Created: {new Date(ticketDetail.created_at).toLocaleDateString()}</span>
              </div>
            </CardHeader>
          </Card>

          {/* Messages */}
          <div className="space-y-3">
            {allMessages.map((msg, i) => {
              const isAdmin = msg.sender_role === "admin";
              return (
                <motion.div key={msg.id || i} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                  <div className={`flex ${isAdmin ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[80%] p-3 rounded-xl ${isAdmin ? "bg-muted border" : "bg-primary text-primary-foreground"}`}
                      data-testid={`ticket-message-${i}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-semibold ${isAdmin ? "text-primary" : "text-primary-foreground/80"}`}>
                          {msg.sender_name}
                        </span>
                        {isAdmin && <Badge className="text-[9px] bg-blue-500/10 text-blue-600">Support</Badge>}
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <p className={`text-[10px] mt-1 ${isAdmin ? "text-muted-foreground" : "text-primary-foreground/60"}`}>
                        {new Date(msg.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Reply */}
          {ticketDetail.status !== "closed" && (
            <Card>
              <CardContent className="p-4">
                <Textarea
                  placeholder="Type your reply..."
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  rows={3}
                  data-testid="ticket-reply-input"
                />
                <div className="flex justify-end mt-3">
                  <Button onClick={sendReply} disabled={sending || !replyText.trim()} data-testid="send-reply-btn">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Send Reply
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Help & Support" variant={variant}>
      <div className="space-y-6" data-testid="learner-support-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Help & Support</h1>
            <p className="text-muted-foreground text-sm">Get help or report issues</p>
          </div>
          <Button onClick={() => setCreateOpen(true)} data-testid="create-ticket-btn">
            <Plus className="w-4 h-4 mr-2" /> New Ticket
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Total", value: stats.total, color: "text-slate-600" },
            { label: "Open", value: stats.open, color: "text-blue-500" },
            { label: "In Progress", value: stats.in_progress, color: "text-amber-500" },
            { label: "Resolved", value: stats.resolved, color: "text-green-500" },
          ].map(s => (
            <Card key={s.label}>
              <CardContent className="p-3 text-center">
                <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-[10px] text-muted-foreground">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search tickets..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 h-9" data-testid="ticket-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px] h-9" data-testid="status-filter">
              <Filter className="w-3 h-3 mr-1" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Ticket List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <LifeBuoy className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-muted-foreground font-medium">No tickets yet</p>
              <p className="text-xs text-muted-foreground mt-1">Create a ticket to get help from our support team</p>
              <Button className="mt-4" onClick={() => setCreateOpen(true)} data-testid="empty-create-ticket-btn">
                <Plus className="w-4 h-4 mr-2" /> Create Ticket
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filtered.map((t, i) => {
              const style = statusStyle[t.status] || statusStyle.open;
              const StatusIcon = style.icon;
              const CatIcon = categoryIcons[t.category] || HelpCircle;
              return (
                <motion.div key={t.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                  <Card
                    className="cursor-pointer hover:border-primary/20 transition-colors"
                    onClick={() => loadTicketDetail(t.id)}
                    data-testid={`ticket-${i}`}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg ${style.bg} flex items-center justify-center shrink-0`}>
                        <CatIcon className={`w-5 h-5 ${style.text}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-sm truncate">{t.subject || t.title}</h4>
                          <Badge className={`${style.bg} ${style.text} text-[9px]`}>
                            <StatusIcon className="w-3 h-3 mr-0.5" /> {style.label}
                          </Badge>
                          {t.priority === "high" && <Badge className="bg-red-500/10 text-red-600 text-[9px]">High</Badge>}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                          <span>{t.ticket_number || t.id?.slice(0,8)}</span>
                          <span>{t.category}</span>
                          <span>{new Date(t.created_at).toLocaleDateString()}</span>
                          <span className="flex items-center gap-0.5">
                            <MessageSquare className="w-3 h-3" /> {t.comment_count || 0}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* FAQ Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" /> Quick Help
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                { q: "How do I start a course?", a: "Go to My Courses, find the course, and click 'Start' or 'Continue'." },
                { q: "Where are my certificates?", a: "Visit Certificates in the sidebar. Only completed courses have certificates." },
                { q: "How do I reset my password?", a: "Go to Settings > Security > Change Password." },
                { q: "Who assigns me courses?", a: "Your organization admin assigns courses based on your role and department." },
              ].map((faq, i) => (
                <div key={i} className="p-3 rounded-lg border bg-muted/20">
                  <p className="text-sm font-medium">{faq.q}</p>
                  <p className="text-xs text-muted-foreground mt-1">{faq.a}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Ticket Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LifeBuoy className="w-5 h-5 text-primary" /> New Support Ticket
            </DialogTitle>
            <DialogDescription>Describe your issue and our team will respond as soon as possible.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Subject</Label>
              <Input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Brief summary of your issue" className="mt-1" data-testid="ticket-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={newCategory} onValueChange={setNewCategory}>
                  <SelectTrigger className="mt-1" data-testid="ticket-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="technical">Technical Issue</SelectItem>
                    <SelectItem value="course_content">Course Content</SelectItem>
                    <SelectItem value="account">Account</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={newPriority} onValueChange={setNewPriority}>
                  <SelectTrigger className="mt-1" data-testid="ticket-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newDesc}
                onChange={e => setNewDesc(e.target.value)}
                placeholder="Describe your issue in detail..."
                rows={4}
                className="mt-1"
                data-testid="ticket-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createTicket} disabled={creating || !newTitle.trim() || !newDesc.trim()} data-testid="submit-ticket-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Submit Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default LearnerSupportPage;
